// G.4 — Recurring Freight Series · admin page.
//
// Two-pane layout: templates list on the left, detail+schedules on the
// right. Forwarder-only. Customer-tier users get a 403 from the API and
// see the empty error state.
import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Repeat2, Plus, Trash2, Play, Pause, Square, Zap, AlertCircle, Loader2,
  Calendar, Clock, ExternalLink, X,
} from "lucide-react";
import { toast } from "sonner";

import {
  useRecurringTemplates, useRecurringTemplate, useRecurringChildren,
  useCreateTemplate, usePatchTemplate, useDeleteTemplate,
  useCreateSchedule, usePatchSchedule, useDeleteSchedule, useTickSchedule,
} from "../api/recurringFreight";
import RecurringSeriesPerfWidget from "../components/RecurringSeriesPerfWidget";

const mono = (size, color, weight, ls) => ({
  fontFamily: "'DM Mono', monospace",
  fontSize: size,
  color: color || "var(--text, #f0f2f8)",
  fontWeight: weight || 400,
  letterSpacing: ls,
});

const STATUS_COLOR = {
  active: "#0a8754",
  paused: "#f3b14a",
  stopped: "#94a3b8",
};

const Pill = ({ tone, children, testid }) => (
  <span
    className="inline-flex items-center px-2 py-0.5 rounded"
    style={{
      background: `${tone}15`,
      color: tone,
      border: `1px solid ${tone}55`,
      ...mono(9, tone, 700, "0.6px"),
      textTransform: "uppercase",
    }}
    data-testid={testid}
  >
    {children}
  </span>
);

const Card = ({ children, testid, onClick, selected }) => (
  <div
    onClick={onClick}
    className={`p-3 rounded cursor-pointer transition-colors ${selected ? "ring-1" : ""}`}
    style={{
      background: selected ? "rgba(24,95,165,0.15)" : "rgba(255,255,255,0.04)",
      border: `1px solid ${selected ? "rgba(24,95,165,0.55)" : "rgba(255,255,255,0.10)"}`,
    }}
    data-testid={testid}
  >
    {children}
  </div>
);

const inputStyle = {
  background: "var(--bg, #0a0e1c)",
  border: "1px solid rgba(255,255,255,0.20)",
  color: "var(--text, #f0f2f8)",
  ...mono(11),
  padding: "6px 9px",
  borderRadius: 4,
  width: "100%",
};

const _isoLocal = (iso) => {
  if (!iso) return "—";
  return String(iso).replace("T", " ").slice(0, 16);
};

// ─── New template modal ────────────────────────────────────────────────────

const NewTemplateModal = ({ onClose, onCreated }) => {
  const [name, setName] = useState("");
  const [dealName, setDealName] = useState("");
  const [pickupCountry, setPickupCountry] = useState("DE");
  const [pickupCity, setPickupCity] = useState("Munich");
  const [dropoffCountry, setDropoffCountry] = useState("HU");
  const [dropoffCity, setDropoffCity] = useState("Budapest");
  const [requestType, setRequestType] = useState("spot");
  const create = useCreateTemplate();

  const submit = async () => {
    if (!name.trim() || !dealName.trim()) {
      toast.error("Template name and deal name are required");
      return;
    }
    try {
      const t = await create.mutateAsync({
        template_name: name.trim(),
        template_data: {
          deal_name: dealName.trim(),
          request_type: requestType,
          pickups: [{ country: pickupCountry, city: pickupCity }],
          dropoffs: [{ country: dropoffCountry, city: dropoffCity }],
        },
      });
      toast.success(`Template "${t.template_name}" created`);
      onCreated && onCreated(t);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Failed to create template");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: "rgba(0,0,0,0.6)" }}
      data-testid="recurring-new-template-modal"
      onClick={onClose}
    >
      <div
        className="rounded-lg p-5"
        style={{ background: "var(--bg-surface, #0d0f18)", border: "1px solid rgba(255,255,255,0.12)", width: 480 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <h3 style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 16 }}>New recurring template</h3>
          <button type="button" onClick={onClose} style={{ background: "transparent", border: "none", color: "#94a3b8", cursor: "pointer" }}>
            <X size={16} />
          </button>
        </div>
        <div className="flex flex-col gap-2.5">
          <label style={mono(9, "#94a3b8", 700, "0.5px")}>TEMPLATE NAME
            <input value={name} onChange={(e) => setName(e.target.value)} style={{ ...inputStyle, marginTop: 4 }} data-testid="recurring-tpl-name-input" />
          </label>
          <label style={mono(9, "#94a3b8", 700, "0.5px")}>DEAL NAME (carried into each generated child)
            <input value={dealName} onChange={(e) => setDealName(e.target.value)} style={{ ...inputStyle, marginTop: 4 }} data-testid="recurring-tpl-deal-name-input" />
          </label>
          <label style={mono(9, "#94a3b8", 700, "0.5px")}>REQUEST TYPE
            <select value={requestType} onChange={(e) => setRequestType(e.target.value)} style={{ ...inputStyle, marginTop: 4 }} data-testid="recurring-tpl-req-type-input">
              <option value="spot">spot</option>
              <option value="tender">tender</option>
              <option value="sonderfahrt">sonderfahrt</option>
            </select>
          </label>
          <div className="grid grid-cols-2 gap-2">
            <label style={mono(9, "#94a3b8", 700, "0.5px")}>PICKUP COUNTRY
              <input value={pickupCountry} onChange={(e) => setPickupCountry(e.target.value)} style={{ ...inputStyle, marginTop: 4 }} data-testid="recurring-tpl-pickup-country-input" maxLength={2} />
            </label>
            <label style={mono(9, "#94a3b8", 700, "0.5px")}>PICKUP CITY
              <input value={pickupCity} onChange={(e) => setPickupCity(e.target.value)} style={{ ...inputStyle, marginTop: 4 }} data-testid="recurring-tpl-pickup-city-input" />
            </label>
            <label style={mono(9, "#94a3b8", 700, "0.5px")}>DROPOFF COUNTRY
              <input value={dropoffCountry} onChange={(e) => setDropoffCountry(e.target.value)} style={{ ...inputStyle, marginTop: 4 }} data-testid="recurring-tpl-dropoff-country-input" maxLength={2} />
            </label>
            <label style={mono(9, "#94a3b8", 700, "0.5px")}>DROPOFF CITY
              <input value={dropoffCity} onChange={(e) => setDropoffCity(e.target.value)} style={{ ...inputStyle, marginTop: 4 }} data-testid="recurring-tpl-dropoff-city-input" />
            </label>
          </div>
        </div>
        <div className="mt-4 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 rounded" style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.15)", color: "#c5cad8", ...mono(10, undefined, 700) }}>Cancel</button>
          <button
            onClick={submit}
            disabled={create.isPending}
            className="px-3 py-1.5 rounded inline-flex items-center gap-1"
            style={{ background: "#185FA5", border: "none", color: "#fff", ...mono(10, "#fff", 700, "0.4px"), opacity: create.isPending ? 0.7 : 1 }}
            data-testid="recurring-tpl-create-submit"
          >
            {create.isPending && <Loader2 size={12} className="animate-spin" />}
            Create template
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── New schedule modal ────────────────────────────────────────────────────

const NewScheduleModal = ({ tid, onClose }) => {
  const [scheduleType, setScheduleType] = useState("weekly");
  const [time, setTime] = useState("08:00");
  const [daysOfWeek, setDaysOfWeek] = useState(["mon", "wed", "fri"]);
  const [dates, setDates] = useState("");
  const [cron, setCron] = useState("0 8 * * 1");
  const [timezone, setTimezone] = useState("Europe/Budapest");
  const create = useCreateSchedule(tid);

  const toggleDay = (d) => {
    setDaysOfWeek((prev) => prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d]);
  };

  const submit = async () => {
    let config = {};
    if (scheduleType === "daily") config = { time };
    else if (scheduleType === "weekly") config = { days_of_week: daysOfWeek, time };
    else if (scheduleType === "specific_dates") config = { dates: dates.split(",").map((s) => s.trim()).filter(Boolean), time };
    else if (scheduleType === "cron") config = { cron };
    try {
      await create.mutateAsync({
        schedule_type: scheduleType,
        schedule_config: config,
        timezone,
      });
      toast.success("Schedule created");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Failed to create schedule");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: "rgba(0,0,0,0.6)" }}
      data-testid="recurring-new-schedule-modal"
      onClick={onClose}
    >
      <div
        className="rounded-lg p-5"
        style={{ background: "var(--bg-surface, #0d0f18)", border: "1px solid rgba(255,255,255,0.12)", width: 500 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <h3 style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 16 }}>New schedule</h3>
          <button type="button" onClick={onClose} style={{ background: "transparent", border: "none", color: "#94a3b8", cursor: "pointer" }}>
            <X size={16} />
          </button>
        </div>
        <div className="flex flex-col gap-2.5">
          <label style={mono(9, "#94a3b8", 700, "0.5px")}>SCHEDULE TYPE
            <select value={scheduleType} onChange={(e) => setScheduleType(e.target.value)} style={{ ...inputStyle, marginTop: 4 }} data-testid="recurring-sched-type-input">
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="specific_dates">Specific dates</option>
              <option value="cron">Cron (5-field)</option>
            </select>
          </label>
          {(scheduleType === "daily" || scheduleType === "weekly" || scheduleType === "specific_dates") && (
            <label style={mono(9, "#94a3b8", 700, "0.5px")}>TIME (HH:MM, local timezone)
              <input value={time} onChange={(e) => setTime(e.target.value)} placeholder="HH:MM" style={{ ...inputStyle, marginTop: 4 }} data-testid="recurring-sched-time-input" />
            </label>
          )}
          {scheduleType === "weekly" && (
            <div>
              <div style={mono(9, "#94a3b8", 700, "0.5px")}>DAYS OF WEEK</div>
              <div className="flex gap-1 mt-1">
                {["mon", "tue", "wed", "thu", "fri", "sat", "sun"].map((d) => (
                  <button
                    key={d}
                    type="button"
                    onClick={(e) => { e.stopPropagation(); toggleDay(d); }}
                    className="px-2.5 py-1 rounded"
                    style={{
                      background: daysOfWeek.includes(d) ? "rgba(24,95,165,0.20)" : "transparent",
                      border: `1px solid ${daysOfWeek.includes(d) ? "rgba(24,95,165,0.55)" : "rgba(255,255,255,0.18)"}`,
                      color: daysOfWeek.includes(d) ? "#9ec3ec" : "#94a3b8",
                      ...mono(10, undefined, 700, "0.4px"),
                      textTransform: "uppercase",
                      cursor: "pointer",
                    }}
                    data-testid={`recurring-sched-day-${d}`}
                  >
                    {d}
                  </button>
                ))}
              </div>
            </div>
          )}
          {scheduleType === "specific_dates" && (
            <label style={mono(9, "#94a3b8", 700, "0.5px")}>DATES (comma-separated YYYY-MM-DD)
              <input value={dates} onChange={(e) => setDates(e.target.value)} placeholder="2026-03-01, 2026-03-15" style={{ ...inputStyle, marginTop: 4 }} data-testid="recurring-sched-dates-input" />
            </label>
          )}
          {scheduleType === "cron" && (
            <label style={mono(9, "#94a3b8", 700, "0.5px")}>CRON (m h dom mon dow)
              <input value={cron} onChange={(e) => setCron(e.target.value)} placeholder="0 8 * * 1" style={{ ...inputStyle, marginTop: 4 }} data-testid="recurring-sched-cron-input" />
            </label>
          )}
          <label style={mono(9, "#94a3b8", 700, "0.5px")}>TIMEZONE
            <input value={timezone} onChange={(e) => setTimezone(e.target.value)} style={{ ...inputStyle, marginTop: 4 }} data-testid="recurring-sched-tz-input" />
          </label>
        </div>
        <div className="mt-4 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 rounded" style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.15)", color: "#c5cad8", ...mono(10, undefined, 700) }}>Cancel</button>
          <button
            onClick={submit}
            disabled={create.isPending}
            className="px-3 py-1.5 rounded inline-flex items-center gap-1"
            style={{ background: "#185FA5", border: "none", color: "#fff", ...mono(10, "#fff", 700, "0.4px"), opacity: create.isPending ? 0.7 : 1 }}
            data-testid="recurring-sched-create-submit"
          >
            {create.isPending && <Loader2 size={12} className="animate-spin" />}
            Create schedule
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Schedule row ──────────────────────────────────────────────────────────

const ScheduleRow = ({ s, tid }) => {
  const patch = usePatchSchedule(tid);
  const del = useDeleteSchedule(tid);
  const tick = useTickSchedule(tid);
  const tone = STATUS_COLOR[s.status] || "#94a3b8";
  const setStatus = async (status) => {
    try {
      await patch.mutateAsync({ sid: s.id, patch: { status } });
      toast.success(`Schedule ${status}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || `Failed to set ${status}`);
    }
  };
  const onTick = async () => {
    try {
      const res = await tick.mutateAsync(s.id);
      toast.success(`Generated child run #${res.child.run_index || "?"} (${res.child.id})`);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Tick failed");
    }
  };
  const onDelete = async () => {
    if (!window.confirm("Delete this schedule? Past generated deals are preserved.")) return;
    try {
      await del.mutateAsync(s.id);
      toast.success("Schedule deleted");
    } catch (e) {
      toast.error("Delete failed");
    }
  };
  const cfg = s.schedule_config || {};
  return (
    <div
      className="rounded p-3 mb-2"
      style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.10)" }}
      data-testid={`recurring-schedule-row-${s.id}`}
    >
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 flex-wrap">
          <Pill tone={tone} testid={`recurring-schedule-${s.id}-status`}>{s.status}</Pill>
          <Pill tone="#185FA5" testid={`recurring-schedule-${s.id}-type`}>{s.schedule_type}</Pill>
          <span style={mono(10, "#c5cad8")}>{s.timezone}</span>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          {s.status === "active" && (
            <button onClick={() => setStatus("paused")} title="Pause" className="px-2 py-1 rounded inline-flex items-center gap-1" style={{ background: "rgba(243,177,74,0.15)", border: "1px solid rgba(243,177,74,0.35)", color: "#f3b14a", ...mono(9, undefined, 700, "0.4px"), textTransform: "uppercase", cursor: "pointer" }} data-testid={`recurring-schedule-${s.id}-pause`}><Pause size={10} /> Pause</button>
          )}
          {s.status === "paused" && (
            <button onClick={() => setStatus("active")} title="Resume" className="px-2 py-1 rounded inline-flex items-center gap-1" style={{ background: "rgba(10,135,84,0.15)", border: "1px solid rgba(10,135,84,0.35)", color: "#0a8754", ...mono(9, undefined, 700, "0.4px"), textTransform: "uppercase", cursor: "pointer" }} data-testid={`recurring-schedule-${s.id}-resume`}><Play size={10} /> Resume</button>
          )}
          {s.status !== "stopped" && (
            <button onClick={() => setStatus("stopped")} title="Stop" className="px-2 py-1 rounded inline-flex items-center gap-1" style={{ background: "rgba(148,163,184,0.15)", border: "1px solid rgba(148,163,184,0.35)", color: "#94a3b8", ...mono(9, undefined, 700, "0.4px"), textTransform: "uppercase", cursor: "pointer" }} data-testid={`recurring-schedule-${s.id}-stop`}><Square size={10} /> Stop</button>
          )}
          {s.status === "active" && (
            <button onClick={onTick} title="Force one run now" className="px-2 py-1 rounded inline-flex items-center gap-1" style={{ background: "rgba(24,95,165,0.20)", border: "1px solid rgba(24,95,165,0.45)", color: "#9ec3ec", ...mono(9, undefined, 700, "0.4px"), textTransform: "uppercase", cursor: "pointer" }} data-testid={`recurring-schedule-${s.id}-tick`}>
              {tick.isPending ? <Loader2 size={10} className="animate-spin" /> : <Zap size={10} />} Run now
            </button>
          )}
          <button onClick={onDelete} title="Delete schedule" className="px-2 py-1 rounded inline-flex items-center" style={{ background: "transparent", border: "1px solid rgba(220,53,69,0.40)", color: "#ef4444", cursor: "pointer" }} data-testid={`recurring-schedule-${s.id}-delete`}>
            <Trash2 size={10} />
          </button>
        </div>
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1" style={mono(10, "#c5cad8")}>
        {cfg.time && <span><Clock size={10} className="inline mr-1" />{cfg.time}</span>}
        {cfg.days_of_week && <span>Days: {cfg.days_of_week.join(", ")}</span>}
        {cfg.dates && <span>Dates: {(cfg.dates || []).join(", ")}</span>}
        {cfg.cron && <span style={{ fontFamily: "'DM Mono', monospace" }}>cron: {cfg.cron}</span>}
      </div>
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1" style={mono(9, "#94a3b8", 700, "0.4px")}>
        <span><Calendar size={9} className="inline mr-1" />Next run: <span style={mono(10, "#9ec3ec", 700)} data-testid={`recurring-schedule-${s.id}-next-run`}>{_isoLocal(s.next_run_at)}</span></span>
        <span>Last run: {_isoLocal(s.last_run_at)}</span>
        <span>Runs: <span style={mono(10, "#0a8754", 700)} data-testid={`recurring-schedule-${s.id}-runs-count`}>{s.runs_count || 0}</span></span>
      </div>
    </div>
  );
};

// ─── Detail pane ───────────────────────────────────────────────────────────

const TemplateDetail = ({ tid }) => {
  const { data: detail, isLoading, error } = useRecurringTemplate(tid);
  const { data: children = [] } = useRecurringChildren(tid);
  const patch = usePatchTemplate(tid);
  const del = useDeleteTemplate();
  const [showScheduleModal, setShowScheduleModal] = useState(false);

  if (!tid) {
    return (
      <div data-testid="recurring-detail-empty" style={{ ...mono(11, "#94a3b8"), padding: 24, textAlign: "center", border: "1px dashed rgba(255,255,255,0.12)", borderRadius: 6 }}>
        Select a template on the left to see its schedules and generated freight deals.
      </div>
    );
  }
  if (isLoading) return <div style={mono(11, "#94a3b8")}><Loader2 size={12} className="inline animate-spin mr-2" />Loading…</div>;
  if (error) return <div style={mono(11, "#ef4444")} data-testid="recurring-detail-error"><AlertCircle size={12} className="inline mr-1" />Failed to load template.</div>;

  const tdata = detail.template_data || {};
  const onToggleActive = async () => {
    try {
      await patch.mutateAsync({ is_active: !detail.is_active });
      toast.success(detail.is_active ? "Template deactivated" : "Template activated");
    } catch (e) { toast.error("Failed to toggle"); }
  };
  const onDelete = async () => {
    if (!window.confirm("Delete this template? Schedules are removed; generated freight deals are preserved.")) return;
    try { await del.mutateAsync(tid); toast.success("Template deleted"); }
    catch (e) { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="recurring-template-detail">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 style={{ fontFamily: "Syne, sans-serif", fontWeight: 800, fontSize: 22 }} data-testid="recurring-template-detail-name">{detail.template_name}</h2>
          <div className="mt-1" style={mono(10, "#94a3b8")}>{detail.id}</div>
        </div>
        <div className="flex items-center gap-2">
          <Pill tone={detail.is_active ? "#0a8754" : "#94a3b8"} testid="recurring-template-detail-active">{detail.is_active ? "active" : "inactive"}</Pill>
          <button onClick={onToggleActive} className="px-2.5 py-1 rounded" style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.18)", color: "#c5cad8", ...mono(9, undefined, 700, "0.4px"), textTransform: "uppercase", cursor: "pointer" }} data-testid="recurring-template-toggle-active">
            {detail.is_active ? "Deactivate" : "Activate"}
          </button>
          <button onClick={onDelete} className="px-2.5 py-1 rounded inline-flex items-center gap-1" style={{ background: "transparent", border: "1px solid rgba(220,53,69,0.40)", color: "#ef4444", ...mono(9, undefined, 700, "0.4px"), textTransform: "uppercase", cursor: "pointer" }} data-testid="recurring-template-delete">
            <Trash2 size={10} /> Delete
          </button>
        </div>
      </div>

      <div className="rounded p-3 mb-4" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.10)" }}>
        <div style={mono(9, "#94a3b8", 700, "0.5px")}>TEMPLATE DEAL SHAPE</div>
        <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1" style={mono(11)}>
          <span data-testid="recurring-template-detail-deal-name">Deal name: <b>{tdata.deal_name}</b></span>
          <span>Type: <b>{tdata.request_type}</b></span>
          {tdata.incoterm && <span>Incoterm: <b>{tdata.incoterm}</b></span>}
          {(tdata.pickups || []).length > 0 && (
            <span data-testid="recurring-template-detail-route">Route: <b>{tdata.pickups[0].city || tdata.pickups[0].country} → {(tdata.dropoffs || [])[0]?.city || (tdata.dropoffs || [])[0]?.country || "?"}</b></span>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between mb-2">
        <h3 style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 14 }}>Schedules ({(detail.schedules || []).length})</h3>
        <button onClick={() => setShowScheduleModal(true)} className="px-2.5 py-1 rounded inline-flex items-center gap-1" style={{ background: "rgba(24,95,165,0.20)", border: "1px solid rgba(24,95,165,0.55)", color: "#9ec3ec", ...mono(10, undefined, 700, "0.4px"), textTransform: "uppercase", cursor: "pointer" }} data-testid="recurring-add-schedule-btn">
          <Plus size={10} /> Add schedule
        </button>
      </div>
      {(detail.schedules || []).length === 0 ? (
        <div style={{ ...mono(11, "#94a3b8"), padding: 16, textAlign: "center", border: "1px dashed rgba(255,255,255,0.12)", borderRadius: 6 }} data-testid="recurring-schedules-empty">
          No schedules yet — add one to start generating recurring deals.
        </div>
      ) : (
        <div data-testid="recurring-schedules-list">
          {(detail.schedules || []).map((s) => <ScheduleRow key={s.id} s={s} tid={tid} />)}
        </div>
      )}

      <h3 className="mt-5 mb-2" style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 14 }}>
        Generated deals ({children.length})
      </h3>

      {/* H.4 — Performance widget surfaces below the schedules + above the
          generated-deal list. Hides itself when the template has no runs. */}
      {children.length > 0 && <RecurringSeriesPerfWidget templateId={tid} />}
      {children.length === 0 ? (
        <div style={{ ...mono(11, "#94a3b8"), padding: 16, textAlign: "center", border: "1px dashed rgba(255,255,255,0.12)", borderRadius: 6 }} data-testid="recurring-children-empty">
          No deals generated yet. Click <b>Run now</b> on an active schedule to fire the first one.
        </div>
      ) : (
        <div className="flex flex-col gap-1.5" data-testid="recurring-children-list">
          {children.map((c) => (
            <Link
              key={c.id}
              to={`/logistics-requests/${c.id}`}
              target="_blank"
              rel="noopener"
              className="p-2.5 rounded flex items-center justify-between"
              style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.10)", textDecoration: "none" }}
              data-testid={`recurring-child-row-${c.id}`}
            >
              <div className="flex items-center gap-3 flex-wrap">
                <span style={mono(10, "#94a3b8", 700, "0.4px")}>#{c.recurring_run_index}</span>
                <span style={mono(11, "#f0f2f8", 700)}>{c.deal_name}</span>
                {c.deal_stage && <Pill tone="#1769aa">{c.deal_stage}</Pill>}
                <span style={mono(10, "#94a3b8")}>{(c.pickups || [])[0]?.city || "?"} → {(c.dropoffs || [])[0]?.city || "?"}</span>
              </div>
              <div className="flex items-center gap-2">
                <span style={mono(9, "#94a3b8")}>{_isoLocal(c.created_at)}</span>
                <ExternalLink size={10} color="#9ec3ec" />
              </div>
            </Link>
          ))}
        </div>
      )}

      {showScheduleModal && (
        <NewScheduleModal tid={tid} onClose={() => setShowScheduleModal(false)} />
      )}
    </div>
  );
};

// ─── Main page ─────────────────────────────────────────────────────────────

export default function AdminRecurringSeries() {
  const { data: templates = [], isLoading, error } = useRecurringTemplates();
  const [selectedTid, setSelectedTid] = useState(null);
  const [showNewTpl, setShowNewTpl] = useState(false);
  const sortedTemplates = useMemo(() => [...templates].sort((a, b) => (a.created_at < b.created_at ? 1 : -1)), [templates]);

  return (
    <div data-testid="admin-recurring-series-page">
      <header className="mb-4">
        <h1 style={{ fontFamily: "Syne, sans-serif", fontWeight: 800, fontSize: "clamp(22px, 3vw, 28px)", letterSpacing: "-0.5px" }} data-testid="view-title">
          <Repeat2 size={22} className="inline mr-2" />
          Recurring Freight Series
        </h1>
        <div className="mt-1" style={mono(11, "#94a3b8")}>
          Template → schedule (daily / weekly / specific dates / cron) → auto-generated freight deals · pause/stop without losing history
        </div>
      </header>

      <div className="grid gap-4" style={{ gridTemplateColumns: "minmax(280px, 360px) 1fr" }}>
        <div data-testid="recurring-templates-pane">
          <div className="flex items-center justify-between mb-2">
            <h3 style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 13 }}>Templates ({sortedTemplates.length})</h3>
            <button onClick={() => setShowNewTpl(true)} className="px-2.5 py-1 rounded inline-flex items-center gap-1" style={{ background: "#185FA5", border: "none", color: "#fff", ...mono(10, "#fff", 700, "0.4px"), cursor: "pointer", textTransform: "uppercase" }} data-testid="recurring-new-template-btn">
              <Plus size={10} /> New template
            </button>
          </div>
          {error && (
            <div style={{ ...mono(11, "#ef4444"), padding: 12, border: "1px solid rgba(220,53,69,0.30)", borderRadius: 4 }} data-testid="recurring-templates-error">
              <AlertCircle size={12} className="inline mr-1" />
              Could not load templates ({error?.response?.status || "network"}). You may not have forwarder access.
            </div>
          )}
          {isLoading && <div style={mono(11, "#94a3b8")}><Loader2 size={12} className="inline animate-spin mr-2" />Loading…</div>}
          {!isLoading && !error && sortedTemplates.length === 0 && (
            <div style={{ ...mono(11, "#94a3b8"), padding: 16, textAlign: "center", border: "1px dashed rgba(255,255,255,0.12)", borderRadius: 6 }} data-testid="recurring-templates-empty">
              No templates yet. Click <b>New template</b> to register your first recurring freight pattern.
            </div>
          )}
          <div className="flex flex-col gap-2">
            {sortedTemplates.map((t) => (
              <Card key={t.id} testid={`recurring-template-card-${t.id}`} onClick={() => setSelectedTid(t.id)} selected={selectedTid === t.id}>
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span style={mono(12, "#f0f2f8", 700, "0.2px")}>{t.template_name}</span>
                  <Pill tone={t.is_active ? "#0a8754" : "#94a3b8"} testid={`recurring-template-card-${t.id}-active`}>{t.is_active ? "active" : "inactive"}</Pill>
                </div>
                <div style={mono(10, "#94a3b8")}>
                  Deal: <span style={mono(10, "#c5cad8", 700)}>{(t.template_data || {}).deal_name}</span>
                </div>
                <div className="mt-1 flex items-center gap-3" style={mono(9, "#94a3b8", 700, "0.4px")}>
                  <span>Schedules: <span style={mono(10, "#9ec3ec", 700)}>{t.schedules_count || 0}</span></span>
                  <span>Children: <span style={mono(10, "#0a8754", 700)}>{t.children_count || 0}</span></span>
                </div>
              </Card>
            ))}
          </div>
        </div>

        <div data-testid="recurring-detail-pane">
          <TemplateDetail tid={selectedTid} />
        </div>
      </div>

      {showNewTpl && (
        <NewTemplateModal
          onClose={() => setShowNewTpl(false)}
          onCreated={(t) => setSelectedTid(t.id)}
        />
      )}
    </div>
  );
}
